import React, { useState } from 'react';
import NoteUploader from '../components/NoteUploader';
import FlashcardViewer from '../components/FlashcardViewer';
import DeckManager from '../components/DeckManager';
import { generateFlashcardsAndQuizFromNotes } from '../utils/api';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { ArrowLeft } from 'lucide-react';

export default function NotesGenerator({ targetExam }) {
  const [savedDecks, setSavedDecks] = useLocalStorage('pm_flashcard_decks', []);
  const [activeDeck, setActiveDeck] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('main'); // 'main' | 'study' | 'quiz'
  
  // Practice Quiz State (if user switches to practice test from generated deck)
  const [quizState, setQuizState] = useState({
    questions: [],
    userAnswers: {},
    isSubmitted: false,
    score: 0,
  });

  const handleGenerate = async ({ notesText, exam, targetType, count, difficulty, sourceFileName }) => {
    setIsGenerating(true);
    try {
      const result = await generateFlashcardsAndQuizFromNotes({
        notesText,
        exam,
        targetType,
        count,
        difficulty,
      });

      const newDeck = {
        id: `deck_${Date.now()}`,
        deckTitle: result.deckTitle || `Notes Deck (${sourceFileName})`,
        summary: result.summary || '',
        exam: exam || targetExam || 'General',
        createdAt: new Date().toISOString(),
        sourceFileName,
        flashcards: result.flashcards || [],
        quiz: result.quiz || [],
      };

      const updatedDecks = [newDeck, ...savedDecks];
      setSavedDecks(updatedDecks);
      setActiveDeck(newDeck);
      setViewMode('study');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReviewComplete = (updatedCard) => {
    if (!activeDeck) return;
    const updatedCards = activeDeck.flashcards.map((c) =>
      c.id === updatedCard.id ? updatedCard : c
    );

    const updatedActiveDeck = { ...activeDeck, flashcards: updatedCards };
    setActiveDeck(updatedActiveDeck);

    const updatedDecks = savedDecks.map((d) =>
      d.id === activeDeck.id ? updatedActiveDeck : d
    );
    setSavedDecks(updatedDecks);
  };

  const handleDeleteDeck = (deckId) => {
    if (window.confirm('Are you sure you want to delete this deck?')) {
      const filtered = savedDecks.filter((d) => d.id !== deckId);
      setSavedDecks(filtered);
      if (activeDeck?.id === deckId) {
        setActiveDeck(null);
        setViewMode('main');
      }
    }
  };

  const handleStartQuiz = () => {
    if (!activeDeck?.quiz || activeDeck.quiz.length === 0) return;
    setQuizState({
      questions: activeDeck.quiz,
      userAnswers: {},
      isSubmitted: false,
      score: 0,
    });
    setViewMode('quiz');
  };

  const handleSelectOption = (questionId, optionKey) => {
    if (quizState.isSubmitted) return;
    setQuizState((prev) => ({
      ...prev,
      userAnswers: { ...prev.userAnswers, [questionId]: optionKey },
    }));
  };

  const handleSubmitQuiz = () => {
    let score = 0;
    quizState.questions.forEach((q) => {
      if (quizState.userAnswers[q.id] === q.correct) {
        score++;
      }
    });

    setQuizState((prev) => ({ ...prev, isSubmitted: true, score }));
  };

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-6 animate-in">
      {/* Top Banner Navigation */}
      {viewMode !== 'main' && (
        <div className="flex items-center justify-between bg-dark-700/60 p-4 rounded-2xl border border-white/10 shadow-sm backdrop-blur-xl">
          <button
            onClick={() => setViewMode('main')}
            className="text-xs font-semibold text-white/60 hover:text-saffron-400 flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Notes Generator & Decks
          </button>

          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-white line-clamp-1 hidden sm:inline">
              {activeDeck?.deckTitle}
            </span>

            <div className="flex bg-dark-600/70 p-1 rounded-xl border border-white/5">
              <button
                onClick={() => setViewMode('study')}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                  viewMode === 'study'
                    ? 'bg-saffron-500/20 text-saffron-400 border border-saffron-500/30 shadow-sm'
                    : 'text-white/50 hover:text-white'
                }`}
              >
                Flashcards ({activeDeck?.flashcards?.length || 0})
              </button>
              {activeDeck?.quiz?.length > 0 && (
                <button
                  onClick={handleStartQuiz}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                    viewMode === 'quiz'
                      ? 'bg-saffron-500/20 text-saffron-400 border border-saffron-500/30 shadow-sm'
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  Practice Test ({activeDeck?.quiz?.length || 0})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Generator View */}
      {viewMode === 'main' && (
        <>
          <NoteUploader
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            selectedExam={targetExam}
          />

          <DeckManager
            decks={savedDecks}
            onSelectDeck={(deck) => {
              setActiveDeck(deck);
              setViewMode('study');
            }}
            onDeleteDeck={handleDeleteDeck}
          />
        </>
      )}

      {/* Flashcard Study Mode */}
      {viewMode === 'study' && activeDeck && (
        <div className="bg-dark-800/40 p-6 rounded-2xl border border-white/5 backdrop-blur-xl">
          <div className="mb-6 text-center">
            <h2 className="font-display text-2xl font-bold text-white">{activeDeck.deckTitle}</h2>
            {activeDeck.summary && (
              <p className="text-xs text-white/50 max-w-xl mx-auto mt-1.5">{activeDeck.summary}</p>
            )}
          </div>

          <FlashcardViewer
            deckTitle={activeDeck.deckTitle}
            flashcards={activeDeck.flashcards}
            onReviewComplete={handleReviewComplete}
          />
        </div>
      )}

      {/* Practice Quiz Mode */}
      {viewMode === 'quiz' && activeDeck && (
        <div className="bg-dark-700/60 p-6 rounded-2xl border border-white/10 shadow-sm max-w-3xl mx-auto space-y-6 backdrop-blur-xl">
          <div className="border-b border-white/10 pb-4">
            <h2 className="font-display text-xl font-bold text-white">
              Practice Quiz: {activeDeck.deckTitle}
            </h2>
            <p className="text-xs text-white/50 mt-1">
              Test your retention on concepts generated from your uploaded notes.
            </p>
          </div>

          <div className="space-y-6">
            {quizState.questions.map((q, idx) => {
              const isSelected = quizState.userAnswers[q.id];
              return (
                <div key={q.id || idx} className="p-4 bg-dark-600/50 rounded-xl border border-white/5">
                  <p className="text-sm font-semibold text-white mb-3 leading-relaxed">
                    Q{idx + 1}. {q.question}
                  </p>

                  <div className="space-y-2">
                    {['A', 'B', 'C', 'D'].map((label, optIdx) => {
                      const optionText = q.options?.[optIdx] || `Option ${label}`;
                      const isOptionSelected = isSelected === label;
                      const isCorrectOption = q.correct === label;

                      let btnStyle = "bg-dark-700/60 border-white/5 text-white/80 hover:bg-dark-600 hover:border-white/10 hover:text-white";
                      if (quizState.isSubmitted) {
                        if (isCorrectOption) {
                          btnStyle = "bg-emerald-500/15 border-emerald-500/50 text-emerald-300 font-semibold";
                        } else if (isOptionSelected && !isCorrectOption) {
                          btnStyle = "bg-rose-500/15 border-rose-500/50 text-rose-300";
                        }
                      } else if (isOptionSelected) {
                        btnStyle = "bg-saffron-500/15 border-saffron-500/50 text-saffron-300 font-semibold";
                      }

                      return (
                        <button
                          key={label}
                          onClick={() => handleSelectOption(q.id, label)}
                          className={`w-full p-3 text-xs text-left rounded-xl border transition-all flex items-start gap-2.5 ${btnStyle}`}
                        >
                          <span className="font-mono font-bold">{label}.</span>
                          <span className="flex-1">{optionText}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Explanation box on submit */}
                  {quizState.isSubmitted && q.explanation && (
                    <div className="mt-3 p-3 bg-dark-800/60 border border-white/5 rounded-xl text-xs text-white/70">
                      <span className="font-semibold text-saffron-400 block mb-1">Explanation:</span>
                      {q.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quiz Action Bar */}
          <div className="pt-4 border-t border-white/10 flex justify-between items-center">
            {quizState.isSubmitted ? (
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-white">
                  Score: {quizState.score} / {quizState.questions.length} (
                  {Math.round((quizState.score / quizState.questions.length) * 100)}%)
                </span>
                <button
                  onClick={handleStartQuiz}
                  className="btn-primary text-xs font-semibold py-2 px-4"
                >
                  Retake Quiz
                </button>
              </div>
            ) : (
              <button
                onClick={handleSubmitQuiz}
                disabled={Object.keys(quizState.userAnswers).length < quizState.questions.length}
                className="btn-primary text-xs font-semibold py-2.5 px-6 disabled:opacity-50"
              >
                Submit Practice Test
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
